<template>
  <HalloweenLocationSelectModal
    :model-value="halloween.$state.isShowHalloweenLocationSelectModal"
    :simplified="halloween.$state.isShowExpeditionStartModal || halloween.$state.isShowExpeditionStartedModal"
    @update:model-value="halloween.$state.isShowHalloweenLocationSelectModal = $event"
    @expedition-start="
      (location) => {
        emit('reset');

        halloween.$state.runtimeSelectedLocation = location;
        halloween.$state.isShowHalloweenLocationSelectModal = false;
        halloween.$state.isShowHireCharactersModal = true;
      }
    "
    @closed="emit('reset')"
    @craft-button-click="openCraftingBuilding"
  />

  <HalloweenHireCharacterModal
    v-model="halloween.$state.isShowHireCharactersModal"
    @back="
      halloween.$state.isShowHireCharactersModal = false;
      halloween.$state.isShowHalloweenLocationSelectModal = true;
    "
    @confirm="handleHireCharacters"
  />
  <HalloweenStartExpeditionModal
    v-if="halloween.$state.runtimeSelectedLocation"
    v-model="halloween.$state.isShowExpeditionStartModal"
    :is-submit-disabled="halloween.$state.isWaitingForExpeditionStartTransaction"
    @submit="handleExpeditionStart"
    @closed="
      halloween.$state.isShowHalloweenLocationSelectModal = false;
      halloween.resetRuntimeState();
    "
    @back="
      halloween.$state.isShowHalloweenLocationSelectModal = false;
      halloween.$state.isShowExpeditionStartModal = false;
      halloween.$state.isShowHireCharactersModal = true;
    "
  />

  <HalloweenExpeditionStartedModal
    v-model="halloween.$state.isShowExpeditionStartedModal"
    :location-name="halloween.$state.runtimeSelectedLocation"
    @submit="
      halloween.$state.isShowHalloweenLocationSelectModal = false;
      halloween.$state.isShowExpeditionProgressModal = true;
    "
    @closed="
      halloween.$state.isShowHalloweenLocationSelectModal = false;
      halloween.resetRuntimeState();
    "
  />

  <HalloweenExpeditionModal
    v-if="halloween.$state.runtimeSelectedLocation"
    v-model="halloween.$state.isShowExpeditionProgressModal"
    :location="halloween.$state.runtimeSelectedLocation"
    :logs="
      halloween.$state.statefulUserExpeditions.find((e) => e.location === halloween.$state.runtimeSelectedLocation)
        ?.logs || []
    "
    @closed="
      halloween.$state.isShowExpeditionProgressModal = false;
      halloween.resetRuntimeState();
      emit('reset');
    "
  />

  <HalloweenAvailableExpeditionsModal
    v-model="halloween.$state.isAvailableExpeditionsModalOpen"
    @closed="emit('reset')"
  />

  <HalloweenAuthModal
    v-if="!address || !userData"
    v-model="halloween.$state.isShowHalloweenLoginModal"
    @connected="emit('reset')"
    @closed="emit('reset')"
  />

  <HalloweenWitchWelcomeModal v-model="halloween.$state.isWitchWelcomeModalOpen" @closed="handleWitchModalClosed" />
  <HalloweenExpeditionCompletedModal
    v-model="halloween.$state.isExpeditionCompletedModalOpen"
    @closed="
      halloween.resetRuntimeState();
      emit('reset');
    "
  />
  <HalloweenExpeditionFinalModal
    v-model="halloween.$state.isFinalModalOpen"
    @closed="
      halloween.resetRuntimeState();
      emit('reset');
    "
  />
</template>
<script setup lang="ts">
import HalloweenExpeditionCompletedModal from '~/components/Halloween/modal/HalloweenExpeditionCompletedModal.vue';
import HalloweenExpeditionFinalModal from '~/components/Halloween/modal/HalloweenExpeditionFinalModal.vue';
import HalloweenExpeditionModal from '~/components/Halloween/modal/HalloweenExpeditionModal.vue';
import HalloweenExpeditionStartedModal from '~/components/Halloween/modal/HalloweenExpeditionStartedModal.vue';
import HalloweenLocationSelectModal from '~/components/Halloween/modal/HalloweenLocationSelectModal.vue';
import HalloweenStartExpeditionModal from '~/components/Halloween/modal/HalloweenStartExpeditionModal.vue';
import HalloweenAvailableExpeditionsModal from '~/components/Halloween/modal/HalloweenAvailableExpeditionsModal.vue';
import HalloweenWitchWelcomeModal from '~/components/Halloween/modal/HalloweenWitchWelcomeModal.vue';
import { HalloweenAuthModal, HalloweenHireCharacterModal } from '~/features/halloween';
import { useGoToBuildingBus } from '~/composables/useGoToBuildingBus';
import type { ParsedCharacterData } from '~/types/halloween/expedition';
import type { ItemDescription } from '~/types/halloween/items';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';
import { HalloweenItem } from '~/types/halloween/items';

const halloween = useHalloweenStore();
const { apiUrl } = useEnvs();
const bus = useGoToBuildingBus();
const runtimeConfig = useRuntimeConfig();

const emit = defineEmits(['reset', 'resume', 'pause']);
defineProps<{
  userData: any;
  address?: string;
}>();

const goToBuildingByHash = inject<((hash: string) => void) | null>('goToBuildingByHash', null); //  open crafting building for map

watch(
  () => [
    halloween.$state.isShowHalloweenLoginModal,
    halloween.$state.isShowHireCharactersModal,
    halloween.$state.isShowExpeditionStartModal,
    halloween.$state.isShowExpeditionStartedModal,
    halloween.$state.isShowExpeditionProgressModal,
    halloween.$state.isExpeditionCompletedModalOpen,
    halloween.$state.isFinalModalOpen,
    halloween.$state.isWitchWelcomeModalOpen,
    halloween.$state.isShowHalloweenLocationSelectModal,
    halloween.$state.isAvailableExpeditionsModalOpen
  ],
  (value) => {
    if (value.some((v) => v)) {
      emit('pause');
    } else {
      emit('resume');
    }
  }
);

const handleWitchModalClosed = () => {
  //localStorage.setItem(props.witchWelcomeFlag, 'true');
  halloween.$state.isSuppressingCraftingModal = false;
};

const openCraftingBuilding = async (item: ItemDescription) => {
  halloween.$state.isShowHalloweenLocationSelectModal = false;

  const hashes = await $fetch<{ buildingHashes: string[] }>(
    `/crafting/buildings-hashes?productAddress=${item.address}`,
    {
      baseURL: apiUrl
    }
  );

  const torchItem = halloweenItemRegistry.get(HalloweenItem.Torch);
  const torchAddress = torchItem.address;
  const isAddressMatch = item.address.toLowerCase() === torchAddress.toLowerCase();

  if (hashes.buildingHashes.length >= 1) {
    if (runtimeConfig.public.env === 'prod' && isAddressMatch) {
      goToBuildingByHash?.(hashes.buildingHashes[0]); // temporary hardcode
      bus.emit(hashes.buildingHashes[0]); // temporary hardcode
      return;
    }
    goToBuildingByHash?.(hashes.buildingHashes.at(-1)!);
    bus.emit(hashes.buildingHashes.at(-1)!);
  }
};

function handleHireCharacters(characters: ParsedCharacterData[]) {
  halloween.$state.isShowHireCharactersModal = false;
  emit('reset');
  halloween.$state.isShowExpeditionStartModal = true;
  halloween.$state.isShowHalloweenLocationSelectModal = true;
  halloween.$state.runtimeSelectedCharacters = characters;
}

async function handleExpeditionStart(duration: number) {
  const location = halloween.$state.runtimeSelectedLocation;
  const characters = halloween.$state.runtimeSelectedCharacters;
  halloween.$state.isWaitingForExpeditionStartTransaction = true;
  const result = await halloween.startExpedition(characters, location, duration).catch(() => {
    halloween.$state.isWaitingForExpeditionStartTransaction = false;
    return false;
  });

  halloween.$state.isWaitingForExpeditionStartTransaction = false;

  if (result === false) return;

  halloween.$state.isShowExpeditionStartModal = false;
  halloween.$state.isShowExpeditionStartedModal = true;
  halloween.$state.isShowHalloweenLocationSelectModal = true;

  halloween.resetRuntimeState();
  void halloween.updateUserExpeditions();
}
</script>
